import React from 'react';

import { useIntl } from 'react-intl-next';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import LegacyButton from '@atlaskit/button/standard-button';
import { SpotlightTarget } from '@atlaskit/onboarding';
import { Box, xcss } from '@atlaskit/primitives';
import { fireUIAnalytics } from '@atlassian/analytics-bridge';
import { Wrap } from '@atlassian/eoc-schedule-common';

import { SPOTLIGHT_NAMES } from '../../../../../../common/constants';
import { List } from '../../../../../../common/ui/list';
import { ListItem } from '../../../../../../common/ui/list/list-item';
import { useRotationFlows, useRotationOrder } from '../../../../../../controllers/edit';
import { useActions as usePageActions } from '../../../../../../controllers/page-store';
import {
	useSchedule,
	useTimelineLoading,
} from '../../../../../../controllers/schedule-store/helpers';
import { useMonitorListItems } from '../../../../../../utils/hooks/monitor-list-items';

import { messages } from './messages';
import { Rotation } from './rotation';
import { RotationTitle } from './styled';

const addRotationButtonContainerStyles = xcss({
	paddingRight: 'space.200',
	marginBottom: 'space.500',
	marginLeft: 'space.negative.150',
});

// TODO: modify internalRotations on edit, clone, add, and delete
const RotationList = () => {
	const intlApi = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const [{ id: scheduleId }] = useSchedule();
	const {
		isLoading: orderRotationLoading,
		rotations,
		orderRotation,
	} = useRotationOrder(scheduleId);
	const { openDeleteRotation } = usePageActions();
	const { add, clone, edit, isLoading: getRotationLoading } = useRotationFlows(scheduleId);

	useMonitorListItems({
		droppableId: 'rotations',
		data: rotations ?? [],
		onComplete: ({ startIndex, targetIndex }) => orderRotation(startIndex, targetIndex),
	});

	const [{ isSubsequentLoading }] = useTimelineLoading();

	const addHasSpotlight = !rotations?.length;
	const rotationHasSpotlight = !addHasSpotlight;

	const isRotationsDisabled = isSubsequentLoading || orderRotationLoading || getRotationLoading;

	return (
		<>
			<RotationTitle>{intlApi.formatMessage(messages.rotationsTitle)}</RotationTitle>

			<List variant="rotation" testId="rotation-list-container">
				{rotations?.map(({ id, name }, index) => (
					<Wrap
						within={(e) => (
							<SpotlightTarget name={SPOTLIGHT_NAMES.firstListRotationItem}>{e}</SpotlightTarget>
						)}
						when={index === 0 && rotationHasSpotlight}
					>
						<ListItem
							key={id}
							value={id}
							title={name}
							isDisabled={isRotationsDisabled}
							droppableId="rotations"
							variant="rotation"
							index={index}
							length={rotations.length}
							reorder={(start: number, target: number) => orderRotation(start, target)}
						>
							<Rotation
								id={id}
								name={name}
								isDisabled={isRotationsDisabled}
								onEdit={() => {
									const event = createAnalyticsEvent({});
									fireUIAnalytics(event, 'button clicked', 'editRotation');

									edit(id);
								}}
								onClone={() => {
									const event = createAnalyticsEvent({});
									fireUIAnalytics(event, 'button clicked', 'duplicateRotation');

									clone(id);
								}}
								onDelete={() => {
									const event = createAnalyticsEvent({});
									fireUIAnalytics(event, 'button clicked', 'deleteRotation');

									const rotation = {
										id,
										name,
									};

									const excluded = rotations.slice(0, index).concat(rotations.slice(index + 1));

									const fallback = excluded[index > excluded.length - 1 ? index - 1 : index];

									openDeleteRotation({
										rotation,
										id: [
											`${id}-rotation-options`,
											`${fallback?.id}-rotation-options`,
											'add-rotation-button',
										],
									});
								}}
							/>
						</ListItem>
					</Wrap>
				))}
			</List>
			<Wrap
				within={(e) => (
					<SpotlightTarget name={SPOTLIGHT_NAMES.firstListRotationItem}>{e}</SpotlightTarget>
				)}
				when={addHasSpotlight}
			>
				<Box xcss={addRotationButtonContainerStyles}>
					<LegacyButton
						id="add-rotation-button"
						onClick={(_, analyticsEvent) => {
							fireUIAnalytics(analyticsEvent, 'addRotation');

							add();
						}}
						isDisabled={isRotationsDisabled}
						appearance="link"
					>
						{intlApi.formatMessage(messages.addRotation)}
					</LegacyButton>
				</Box>
			</Wrap>
		</>
	);
};

export { RotationList };
